/**
 * Module-scoped cache for `DiagnosticsReport` results.
 *
 * Per `Ralph/ConnectionDiagnostics/PRD.md` → Slice 3:
 *   "Cached: subsequent opens for the same `(connectionId, trigger,
 *   scopeDatabase)` reuse the cached report (cache invalidates on
 *   `connectionId` reconnect or 5 min TTL, whichever sooner)."
 *
 * Reconnect invalidation is delegated to whoever calls `invalidateForConnection`
 * (currently the dialog itself: it doesn't auto-invalidate on reconnect, but
 * the 5 min TTL handles the staleness case in practice). Keeping this module
 * tiny and synchronous means it stays trivial to unit-test.
 */

import type { DiagnosticsReport } from '../../types/connectionDiagnostics';

const TTL_MS = 5 * 60 * 1000;

type CacheKey = string;
type CacheEntry = {
  report: DiagnosticsReport;
  storedAt: number;
};

const cache = new Map<CacheKey, CacheEntry>();

export type CacheKeyArgs = {
  connectionId: string;
  trigger: string;
  scopeDatabase?: string;
};

export function buildCacheKey(args: CacheKeyArgs): CacheKey {
  return `${args.connectionId}|${args.trigger}|${args.scopeDatabase ?? ''}`;
}

export function readDiagnosticsCache(
  args: CacheKeyArgs,
  now: number = Date.now()
): DiagnosticsReport | undefined {
  const key = buildCacheKey(args);
  const entry = cache.get(key);
  if (!entry) return undefined;
  if (now - entry.storedAt > TTL_MS) {
    cache.delete(key);
    return undefined;
  }
  return entry.report;
}

export function writeDiagnosticsCache(
  args: CacheKeyArgs,
  report: DiagnosticsReport,
  now: number = Date.now()
): void {
  cache.set(buildCacheKey(args), { report, storedAt: now });
}

export function invalidateDiagnosticsCacheForConnection(connectionId: string): void {
  for (const key of cache.keys()) {
    if (key.startsWith(`${connectionId}|`)) {
      cache.delete(key);
    }
  }
}

export function clearDiagnosticsCache(): void {
  cache.clear();
}
